/**
 * The host's body (2026-10-05): a built-in avatar (Louise, navy pantsuit) standing behind the
 * reception counter, animated with the shared Mixamo clips: breathing idle, a wave to greet,
 * talking while it speaks, pointing at what it describes. It turns to face the visitor.
 * The characters have no face rig (see CLAUDE.md, Assets), so speech shows as the talking
 * clip and a lit ring on its name tag.
 */
import * as THREE from 'three';
import { BODY_FONT } from '../ui/theme.ts';
import { retargetForHost, type AvatarLoader } from '../world/avatars.ts';
import type { ArkitFace } from '../avatars/face.ts';
import { MouthDriver } from '../avatars/jaw.ts';
import type { MouthShape } from './hostVoice.ts';
import { HOST_NAME } from './tour.ts';

type Clip = 'breathing-idle' | 'talking' | 'walking' | 'waving' | 'pointing' | 'thumbs-up';
const LOOPS: Clip[] = ['breathing-idle', 'talking', 'walking'];
/** Walking pace, metres per second (the clip's stride matches it). */
export const WALK_SPEED = 1.25;
const FADE = 0.35;
/** How far the host turns from its post to face someone, and how fast. */
const MAX_TURN = (75 * Math.PI) / 180;
const TURN_RATE = 2.4;

export class HostAvatar {
  readonly group = new THREE.Group();
  loaded = false;
  loadError: string | undefined;
  private mixer: THREE.AnimationMixer | undefined;
  private readonly actions = new Map<Clip, THREE.AnimationAction>();
  private base: Clip = 'breathing-idle';
  private gestureAction: THREE.AnimationAction | undefined;
  private readonly tag: THREE.Sprite;
  private readonly tagCanvas = document.createElement('canvas');
  private readonly tagTexture: THREE.CanvasTexture;
  private speaking = false;
  /** Where the host stands when not on a tour, and the way it faces there. */
  private post = { x: 0, z: 0, yaw: 0 };
  /** The way the body faces now, and the way it is turning to. */
  private yaw = 0;
  private targetYaw = 0;
  /** While on a tour the host may face anywhere; at its post it keeps within MAX_TURN. */
  private free = false;
  height = 1.7;
  /** The rest of the current walk, and what to call when it ends or is cut short. */
  private route: { x: number; z: number }[] = [];
  private arrived: ((completed: boolean) => void) | undefined;
  private mouth: MouthDriver | undefined;
  /** Loudness of her voice now (0..1), set each frame by the host. */
  voiceLevel = 0;
  /** The sound being heard now (lip sync), set each frame by the host; absent without timing. */
  mouthShape: MouthShape | undefined;
  private face: ArkitFace | undefined;

  constructor() {
    this.group.name = 'host';
    this.tagCanvas.width = 512;
    this.tagCanvas.height = 128;
    this.tagTexture = new THREE.CanvasTexture(this.tagCanvas);
    this.tagTexture.colorSpace = THREE.SRGBColorSpace;
    this.tag = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.tagTexture,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    this.tag.scale.set(0.56, 0.14, 1);
    this.tag.center.set(0.5, 0);
    this.tag.renderOrder = 50;
    this.group.add(this.tag);
    this.drawTag();
  }

  async load(loader: AvatarLoader): Promise<void> {
    try {
      // Her own body since 2026-10-06: a MetaPerson character with a face rig and teeth
      // (`AvatarLoader.loadHost`); the lobby fetches it while the room code is typed.
      const [body, clips] = await Promise.all([loader.loadHost(), loader.loadAnimations()]);
      this.height = body.height;
      if (body.jaw) this.mouth = new MouthDriver(body.jaw);
      this.face = body.face;
      this.mixer = new THREE.AnimationMixer(body.root);
      for (const name of [
        'breathing-idle',
        'talking',
        'walking',
        'waving',
        'pointing',
        'thumbs-up',
      ] as Clip[]) {
        const clip = clips.find((c) => c.name === name);
        if (!clip) continue;
        // Rotations only, onto her skeleton's names; no root motion walks her off her spot.
        const action = this.mixer.clipAction(retargetForHost(clip));
        if (!LOOPS.includes(name)) {
          action.setLoop(THREE.LoopOnce, 1);
          action.clampWhenFinished = true;
        }
        this.actions.set(name, action);
      }
      this.mixer.addEventListener('finished', (e) => {
        if (e.action === this.gestureAction) this.endGesture();
      });
      this.actions.get(this.base)?.play();
      this.group.add(body.root);
      this.tag.position.set(0, this.height + 0.18, 0);
      this.loaded = true;
    } catch (err) {
      this.loadError = (err as Error).message;
      console.warn('[clinspace] host avatar failed to load:', this.loadError);
    }
  }

  /** The host's post (behind the counter) and its resting facing. */
  setPost(x: number, z: number, yaw: number): void {
    this.post = { x, z, yaw };
    this.free = false;
    this.place(x, z, yaw);
  }

  /** Puts the host somewhere at once (a tour stop), facing `yaw`. */
  place(x: number, z: number, yaw: number): void {
    this.group.position.set(x, 0, z);
    this.yaw = this.targetYaw = yaw;
    this.group.rotation.y = yaw;
  }

  /** Away from the post (the host may turn freely), or back at it. */
  setTouring(on: boolean): void {
    this.free = on;
    if (!on) this.place(this.post.x, this.post.z, this.post.yaw);
  }

  /** Turns toward a point in the room. */
  faceTowards(point: THREE.Vector3): void {
    const p = this.group.position;
    let yaw = Math.atan2(point.x - p.x, point.z - p.z);
    if (!this.free) {
      const off =
        THREE.MathUtils.euclideanModulo(yaw - this.post.yaw + Math.PI, Math.PI * 2) - Math.PI;
      yaw = this.post.yaw + THREE.MathUtils.clamp(off, -MAX_TURN, MAX_TURN);
    }
    this.targetYaw = yaw;
  }

  /** Plays a gesture once, then returns to the idle or talking loop. */
  gesture(name: 'waving' | 'pointing' | 'thumbs-up'): void {
    const action = this.actions.get(name);
    if (!action) return;
    this.gestureAction?.fadeOut(FADE);
    action.reset().setEffectiveWeight(1).fadeIn(FADE).play();
    this.actions.get(this.base)?.fadeOut(FADE);
    this.gestureAction = action;
  }

  setSpeaking(on: boolean): void {
    if (on === this.speaking) return;
    this.speaking = on;
    this.drawTag();
    if (!this.walking) this.setBase(on ? 'talking' : 'breathing-idle');
  }

  get walking(): boolean {
    return this.route.length > 0;
  }

  /**
   * Walks the route (waypoints then the destination) at a walking pace, facing the way she
   * goes. Resolves true on arrival, false when the walk is stopped.
   */
  walk(route: { x: number; z: number }[]): Promise<boolean> {
    this.stopWalking();
    if (!route.length) return Promise.resolve(true);
    this.free = true;
    this.route = route.map((p) => ({ ...p }));
    this.gestureAction?.fadeOut(FADE);
    this.gestureAction = undefined;
    this.setBase('walking');
    return new Promise((resolve) => (this.arrived = resolve));
  }

  stopWalking(): void {
    if (!this.route.length) return;
    this.route = [];
    this.setBase(this.speaking ? 'talking' : 'breathing-idle');
    const done = this.arrived;
    this.arrived = undefined;
    done?.(false);
  }

  /** Back at the post: her resting place and facing. */
  get postSpot(): { x: number; z: number; yaw: number } {
    return { ...this.post };
  }

  /** Where she stands now. */
  get spot(): { x: number; z: number } {
    return { x: this.group.position.x, z: this.group.position.z };
  }

  get isSpeaking(): boolean {
    return this.speaking;
  }

  /** World point of the host's head (for the card and for pointing the visitor at). */
  headPosition(target = new THREE.Vector3()): THREE.Vector3 {
    return target.set(this.group.position.x, this.height - 0.1, this.group.position.z);
  }

  update(dt: number): void {
    this.mixer?.update(dt);
    // After the clips: the mouth is not animated by them, only by her voice. With the voice's
    // timing her lips make each sound (lip sync); without it the jaw follows the loudness.
    const lipSync = this.speaking ? this.mouthShape : undefined;
    const lipsMoving = this.face?.speak(lipSync, dt) ?? false;
    if (!lipSync && !lipsMoving) this.mouth?.update(this.speaking ? this.voiceLevel : 0, dt);
    const next = this.route[0];
    if (next) {
      const p = this.group.position;
      const dx = next.x - p.x;
      const dz = next.z - p.z;
      const d = Math.hypot(dx, dz);
      const step = WALK_SPEED * dt;
      if (d <= step) {
        p.x = next.x;
        p.z = next.z;
        this.route.shift();
        if (!this.route.length) {
          this.setBase(this.speaking ? 'talking' : 'breathing-idle');
          const done = this.arrived;
          this.arrived = undefined;
          done?.(true);
        }
      } else {
        p.x += (dx / d) * step;
        p.z += (dz / d) * step;
        this.targetYaw = Math.atan2(dx, dz);
      }
    }
    const diff =
      THREE.MathUtils.euclideanModulo(this.targetYaw - this.yaw + Math.PI, Math.PI * 2) - Math.PI;
    const step = Math.sign(diff) * Math.min(Math.abs(diff), TURN_RATE * dt);
    this.yaw += step;
    this.group.rotation.y = this.yaw;
  }

  private setBase(clip: Clip): void {
    if (clip === this.base) return;
    const from = this.actions.get(this.base);
    const to = this.actions.get(clip);
    this.base = clip;
    if (this.gestureAction) return;
    to?.reset().fadeIn(FADE).play();
    from?.fadeOut(FADE);
  }

  private endGesture(): void {
    const g = this.gestureAction;
    this.gestureAction = undefined;
    g?.fadeOut(FADE);
    this.actions.get(this.base)?.reset().fadeIn(FADE).play();
  }

  private drawTag(): void {
    const ctx = this.tagCanvas.getContext('2d')!;
    const W = this.tagCanvas.width;
    const H = this.tagCanvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(13,34,48,0.88)';
    ctx.beginPath();
    ctx.roundRect(8, 16, W - 16, H - 32, (H - 32) / 2);
    ctx.fill();
    ctx.lineWidth = this.speaking ? 6 : 2;
    ctx.strokeStyle = this.speaking ? '#8fe3f0' : 'rgba(170,236,248,0.5)';
    ctx.stroke();
    ctx.fillStyle = '#3fd6ff';
    ctx.beginPath();
    ctx.arc(52, H / 2, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#eef6fa';
    ctx.font = `600 40px ${BODY_FONT}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(`${HOST_NAME} · AI host`, 80, H / 2 + 2, W - 110);
    this.tagTexture.needsUpdate = true;
  }

  dispose(): void {
    this.mixer?.stopAllAction();
    this.tagTexture.dispose();
    (this.tag.material as THREE.Material).dispose();
  }
}
